import { useState } from 'react';
import { trackEvent } from '../../../utils/tracking';
import KIAImage from '../ui/image/KIAImage';
import Tooltip from '../ui/Tooltip';
import pvfiveInfo, { CustomerType, LeaseInfo } from './pvfive-info';

const Divider = () => <div className="h-px bg-dark-blue/50 my-4" />;

const InfoCard: React.FC = () => {
  const [customerType, setCustomerType] = useState<CustomerType>('business');

  const info = pvfiveInfo[customerType];

  const {
    title,
    subtitle,
    tag,
    image,
    leases,
    tax,
    pricing,
    links,
    footer,
    detailText,
    priceText,
    extraLink,
  } = info;

  return (
    <div className="max-w-screen-kia-sm mx-auto px-4 lg:px-0">
      <div className="relative group bg-kia-grey p-6 rounded-lg shadow-lg overflow-visible">
        <span className="absolute top-4 right-4 md:left-4 md:right-auto inline-block bg-white text-black text-[13px] px-3 py-1 rounded-full shadow-sm z-10">
          {tag}
        </span>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-7">
          <div className="relative group flex justify-center items-start mt-0 md:mt-5">
            <a
              href={image.href}
              className="block group-hover:scale-105 transition-transform"
            >
              <KIAImage
                src={image.src}
                alt={image.alt}
                width={500}
                className="object-cover"
              />
            </a>
          </div>

          <div className="flex flex-col">
            <a href={image.href} className="no-underline group-hover:underline">
              <h3 className="text-lg font-bold mb-2 text-black">{title}</h3>
            </a>

            <p className="mb-4 inline-block px-4 py-2 bg-white rounded-full shadow-md text-sm text-black">
              {subtitle}
            </p>

            <div className="mb-4 flex rounded-full p-1 bg-white shadow-md">
              <button
                onClick={() => setCustomerType('business')}
                className={`px-4 py-2 flex-1 rounded-full text-sm ${
                  customerType === 'business'
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-white/90'
                }`}
              >
                Företag
              </button>
              <button
                onClick={() => setCustomerType('private')}
                className={`px-4 py-2 flex-1 rounded-full text-sm ${
                  customerType === 'private'
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-white/90'
                }`}
              >
                Privat
              </button>
            </div>

            {leases.length > 0 && (
              <div className="">
                {leases.map((lease: LeaseInfo, idx: number) => (
                  <div
                    key={idx}
                    className="font-semibold text-sm flex items-center"
                  >
                    {lease.headline} {lease.subline}
                    <span className="ml-1 self-start relative -top-1">
                      <Tooltip describe={lease.tooltip}>
                        {lease.tooltip}
                      </Tooltip>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {priceText && <p className="text-sm mb-4">{priceText}</p>}

            {detailText && (
              <p className="text-xs text-gray-700 mb-4 whitespace-pre-line">
                {detailText}
              </p>
            )}

            {customerType === 'private' ? (
              <>
                <div className="grid sm:grid-cols-2 gap-1 sm:gap-4 text-sm text-gray-800 mb-4">
                  <div>
                    <p className="font-semibold text-sm">
                      {pricing.list[0]?.label}
                    </p>
                    <p className="text-sm">{pricing.list[0]?.value}</p>
                  </div>
                  {extraLink && (
                    <div className="flex items-start">
                      <a
                        href={extraLink.href}
                        className="text-xs underline decoration-current transition-all duration-200 ease-in-out hover:text-black/50 hover:decoration-transparent hover:underline-offset-4"
                      >
                        {extraLink.label}
                      </a>
                    </div>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-800">
                  <div>
                    <p className="font-semibold text-sm">{tax.label}</p>
                    <p className="text-sm">{tax.value}</p>
                  </div>
                </div>
                {links[0] && (
                  <a
                    href={links[0].href}
                    className="mt-4 bg-black underline-offset-2 no-underline text-white py-3 text-center rounded-sm border border-black hover:bg-transparent hover:underline hover:text-black transition-colors"
                  >
                    {links[0].label}
                  </a>
                )}
              </>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-800 mb-4">
                  <div>
                    <p className="font-semibold text-sm">
                      {pricing.list[0]?.label}
                    </p>
                    <p className="text-sm mb-2">{pricing.list[0]?.value}</p>
                    {extraLink && (
                      <a
                        href={extraLink.href}
                        className="text-xs underline decoration-current transition-all duration-200 ease-in-out hover:text-black/50 hover:decoration-transparent hover:underline-offset-4"
                      >
                        {extraLink.label}
                      </a>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">
                      {pricing.list[1]?.label}
                    </p>
                    <p className="text-sm mb-2">{pricing.list[1]?.value}</p>
                    {links[1] && (
                      <a
                        href={links[1].href}
                        className="text-xs underline decoration-current transition-all duration-200 ease-in-out hover:text-black/50 hover:decoration-transparent hover:underline-offset-4"
                      >
                        {links[1].label}
                      </a>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-800">
                  <div>
                    <p className="font-semibold text-sm">{tax.label}</p>
                    <p className="text-sm">{tax.value}</p>
                  </div>
                </div>
                {links[0] && (
                  <a
                    href={links[0].href}
                    className="mt-4 bg-black underline-offset-2 no-underline text-white py-3 text-center rounded-sm border border-black hover:bg-transparent hover:underline hover:text-black transition-colors"
                  >
                    {links[0].label}
                  </a>
                )}
                <Divider />
              </>
            )}

            {footer.map((line: string, i: number) => {
              if (line.includes('Kia-återförsäljare')) {
                const [before, after] = line.split('Kia-återförsäljare');

                return (
                  <p key={i} className="text-xs mt-2">
                    {before}

                    <a
                      href="https://www.kia.com/se/sok-aterforsaljare-verkstad"
                      onClick={() =>
                        trackEvent({ event: 'find_a_dealer', model: undefined })
                      }
                      className="text-xs underline decoration-current transition-all duration-200 ease-in-out hover:text-black/50 hover:decoration-transparent hover:underline-offset-4"
                    >
                      Kia-återförsäljare
                    </a>

                    {after}
                  </p>
                );
              }

              return (
                <p key={i} className="text-xs">
                  {line}
                </p>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoCard;
